// src/admin/ReviewManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Plus, Edit, Trash2, X, Save, Star, AlertCircle,
  CheckCircle, XCircle, MessageCircle, Filter, Search
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const ReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const defaultFormData = {
    customerName: '',
    customerEmail: '',
    productTitle: '',
    rating: 5,
    comment: '',
    approved: true,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'reviews'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.customerName || !formData.comment) {
      setMessage({ type: 'error', text: 'Naam aur comment zaroori hai.' });
      return;
    }

    const reviewData = {
      customerName: formData.customerName,
      customerEmail: formData.customerEmail || '',
      productTitle: formData.productTitle || '',
      productId: editingReview?.productId || '',
      rating: Number(formData.rating) || 5,
      comment: formData.comment,
      approved: formData.approved === true,
    };

    try {
      if (editingReview) {
        await updateDoc(doc(db, 'reviews', editingReview.id), reviewData);
        setSuccessMessage('Review updated!');
      } else {
        const newId = Date.now().toString();
        await setDoc(doc(db, 'reviews', newId), {
          ...reviewData,
          userId: 'admin-added',
          createdAt: new Date(),
        });
        setSuccessMessage('Review added!');
      }
      setShowModal(false);
      setShowSuccess(true);
      fetchReviews();
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save review.' });
    }
  };

  const handleDelete = async (review) => {
    if (!window.confirm(`Delete review from "${review.customerName}"?`)) return;
    try {
      await deleteDoc(doc(db, 'reviews', review.id));
      setSuccessMessage('Review deleted!');
      setShowSuccess(true);
      fetchReviews();
    } catch (error) {
      alert('Failed to delete.');
    }
  };

  const toggleApproval = async (review) => {
    try {
      await updateDoc(doc(db, 'reviews', review.id), {
        approved: !review.approved,
      });
      setSuccessMessage(review.approved ? 'Review rejected!' : 'Review approved!');
      setShowSuccess(true);
      fetchReviews();
    } catch (error) {
      console.error(error);
    }
  };

  const openAddModal = () => {
    setEditingReview(null);
    setFormData(defaultFormData);
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setFormData({
      customerName: review.customerName || '',
      customerEmail: review.customerEmail || '',
      productTitle: review.productTitle || '',
      rating: review.rating || 5,
      comment: review.comment || '',
      approved: review.approved === true,
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const getFilteredReviews = () => {
    let filtered = [...reviews];

    if (filter === 'pending') filtered = filtered.filter(r => !r.approved);
    else if (filter === 'approved') filtered = filtered.filter(r => r.approved);
    else if (filter === 'rejected') filtered = filtered.filter(r => !r.approved);

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.customerName?.toLowerCase().includes(term) ||
        r.customerEmail?.toLowerCase().includes(term) ||
        r.comment?.toLowerCase().includes(term) ||
        r.productTitle?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const filteredReviews = getFilteredReviews();

  const counts = {
    all: reviews.length,
    pending: reviews.filter(r => !r.approved).length,
    approved: reviews.filter(r => r.approved).length,
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-PK', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MessageCircle className="text-green-700" size={26} /> Reviews Management
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Customer reviews approve/reject karein ya manually add karein.
          </p>
        </div>
        <button onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md">
          <Plus size={18} /> Add Review
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All', count: counts.all },
            { key: 'pending', label: 'Pending Approval', count: counts.pending },
            { key: 'approved', label: 'Approved', count: counts.approved },
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                filter === tab.key ? 'bg-green-700 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        <div className="relative">
          <input type="text" placeholder="Search by name, email, ya comment..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm" />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading reviews...</p>
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <MessageCircle size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No reviews found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredReviews.map(review => (
            <div key={review.id} className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: '#16a34a' }}>
                    {review.customerName?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-800">{review.customerName || 'Anonymous'}</p>
                    <p className="text-[10px] text-gray-500">{formatDate(review.createdAt)}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                  review.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {review.approved ? 'APPROVED' : 'PENDING'}
                </span>
              </div>

              {/* Stars */}
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14}
                    className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                ))}
                <span className="text-xs font-bold ml-1">{review.rating}/5</span>
              </div>

              {/* Product */}
              {review.productTitle && (
                <p className="text-[11px] mb-2 px-2 py-1 rounded bg-green-50 text-green-700 inline-block">
                  📦 {review.productTitle}
                </p>
              )}

              {/* Comment */}
              <p className="text-sm text-gray-600 mb-4 line-clamp-4">
                "{review.comment}"
              </p>

              {/* Actions */}
              <div className="flex gap-2">
                <button onClick={() => toggleApproval(review)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 ${
                    review.approved
                      ? 'bg-orange-50 hover:bg-orange-100 text-orange-700'
                      : 'bg-green-50 hover:bg-green-100 text-green-700'
                  }`}>
                  {review.approved ? (
                    <><XCircle size={12} /> Reject</>
                  ) : (
                    <><CheckCircle size={12} /> Approve</>
                  )}
                </button>
                <button onClick={() => openEditModal(review)}
                  className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600">
                  <Edit size={14} />
                </button>
                <button onClick={() => handleDelete(review)}
                  className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">
                {editingReview ? 'Edit Review' : 'Add Review'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  <AlertCircle size={14} />
                  {message.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Name *</label>
                <input type="text" value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="Ahmed Khan" required />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Email</label>
                <input type="email" value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="ahmed@example.com" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Product (Optional)</label>
                <input type="text" value={formData.productTitle}
                  onChange={(e) => setFormData({ ...formData, productTitle: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="Fresh Organic Apples" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Rating *</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button key={star} type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="transition hover:scale-125">
                      <Star size={28} className={star <= formData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                    </button>
                  ))}
                  <span className="ml-2 font-bold">{formData.rating}/5</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Review Comment *</label>
                <textarea value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                  placeholder="Product review..." required />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.approved}
                  onChange={(e) => setFormData({ ...formData, approved: e.target.checked })}
                  className="w-5 h-5 text-green-700" />
                <span className="text-sm font-semibold text-gray-700">Approved (show on website)</span>
              </label>

              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition flex items-center justify-center gap-2">
                  <Save size={18} /> {editingReview ? 'Update' : 'Create'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default ReviewManagement;